import React, { useState } from "react";
import {
  Phone, MessageSquare, Database, RefreshCw, CalendarCheck,
  Globe, Search, BarChart3, Megaphone, Link2, Bell,
  PhoneForwarded, Users, TrendingUp, Bot, FileText
} from "lucide-react";

const axes = [
  {
    id: "acquisition",
    number: "01",
    icon: TrendingUp,
    title: "On vous trouve des nouveaux clients",
    subtitle: "80% des artisans manquent de clients. On remplit votre agenda.",
    color: "accent",
    services: [
      {
        icon: Search,
        title: "Google Ads",
        description: "Captez les clients qui cherchent un artisan maintenant. Idéal pour les métiers d'urgence : plombier, serrurier, électricien.",
        tags: ["Recherche active", "Local", "Multi-villes"],
      },
      {
        icon: Megaphone,
        title: "Facebook & Meta Ads",
        description: "Créez l'envie et captez des leads via formulaires instantanés. Idéal pour cuisiniste, peintre, paysagiste, rénovation.",
        tags: ["Retargeting", "Visuels", "Géo-ciblage"],
      },
      {
        icon: Globe,
        title: "Site Web + SEO Local",
        description: "Un site vitrine professionnel optimisé pour le référencement local. Soyez trouvé en premier dans votre zone.",
        tags: ["Vitrine", "SEO", "Multi-ville"],
      },
    ],
  },
  {
    id: "callbot",
    number: "02",
    icon: Bot,
    title: "Le call bot répond à votre place",
    subtitle: "Sur chantier, au volant, les mains occupées — plus aucun appel perdu.",
    color: "primary",
    services: [
      {
        icon: Phone,
        title: "IA Réponse Appels 24/7",
        description: "Votre standard intelligent. L'IA décroche, qualifie la demande, collecte les infos et vous transmet les leads chauds.",
        tags: ["24/7", "Qualification", "Multi-lignes"],
      },
      {
        icon: PhoneForwarded,
        title: "Rappel Appels Manqués",
        description: "SMS, appel vocal ou WhatsApp automatique dès qu'un appel est manqué. Le client ne part jamais chez le concurrent.",
        tags: ["SMS", "Vocal", "WhatsApp"],
      },
      {
        icon: Bell,
        title: "Notifications Temps Réel",
        description: "Recevez un résumé instantané de chaque interaction : appels, messages, demandes. Gardez le contrôle même sur chantier.",
        tags: ["WhatsApp", "Résumé", "Temps réel"],
      },
      {
        icon: CalendarCheck,
        title: "Prise de RDV Automatique",
        description: "L'IA propose des créneaux, confirme le rendez-vous et dispatche à l'équipe si besoin. Plus de ping-pong téléphonique.",
        tags: ["Agenda", "Dispatch", "Confirmation"],
      },
    ],
  },
  {
    id: "gestion",
    number: "03",
    icon: FileText,
    title: "On gère vos relances et votre suivi",
    subtitle: "Devis non signés, factures impayées, avis Google — tout est automatisé.",
    color: "primary",
    services: [
      {
        icon: RefreshCw,
        title: "Relance Devis Automatique",
        description: "Vos devis non signés sont relancés par SMS et WhatsApp avec tracking. Fini les devis oubliés dans la boîte mail.",
        tags: ["SMS", "WhatsApp", "Tracking"],
      },
      {
        icon: MessageSquare,
        title: "WhatsApp Automation",
        description: "Confirmations de RDV, rappels, demande d'avis Google, relance factures — tout passe par WhatsApp automatiquement.",
        tags: ["Factures", "Avis Google", "Rappels"],
      },
      {
        icon: Database,
        title: "CRM Artisan",
        description: "Centralisez tous vos contacts, appels, devis et relances dans un seul outil pensé pour les artisans.",
        tags: ["Pipeline", "Suivi", "Équipe"],
      },
      {
        icon: Link2,
        title: "Intégration CRM Externe",
        description: "Vous gardez votre CRM (Bexio, HubSpot, Pipedrive, Salesforce, Odoo) et on y connecte toute l'infrastructure IA.",
        tags: ["API", "Sur mesure", "Synchronisé"],
      },
      {
        icon: BarChart3,
        title: "Reporting & Tableau de Bord",
        description: "Dashboard live avec métriques clés : leads, appels, devis, conversions. Rapport mensuel + appel stratégie.",
        tags: ["Dashboard", "Rapport", "Stratégie"],
      },
    ],
  },
];

export const Solution = () => {
  const [openAxe, setOpenAxe] = useState("acquisition");

  return (
    <section id="solution" className="py-14 lg:py-28 blueprint-bg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 lg:mb-16">
          <p className="section-label text-primary mb-4">Nos solutions</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mb-6 tracking-tight">
            3 axes pour{" "}
            <span className="text-accent">transformer votre activité</span>
          </h2>
          <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto">
            Plus de clients, zéro appel perdu, des relances qui tournent toutes seules.
          </p>
        </div>

        {/* 3 Axes Tabs */}
        <div className="grid md:grid-cols-3 gap-4 mb-10" data-testid="solution-axes">
          {axes.map((axe) => {
            const Icon = axe.icon;
            const isActive = openAxe === axe.id;
            return (
              <button
                key={axe.id}
                onClick={() => setOpenAxe(axe.id)}
                data-testid={`axe-${axe.id}`}
                className={`text-left p-5 rounded-md border-2 transition-all duration-300 ${
                  isActive
                    ? "bg-white border-accent shadow-lg"
                    : "bg-white/60 border-slate-200 hover:border-primary/40"
                }`}
                style={isActive ? { boxShadow: '4px 4px 0px rgba(249, 115, 22, 0.2)' } : {}}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className={`text-xs font-black ${isActive ? "text-accent" : "text-slate-400"}`}>
                    {axe.number}
                  </span>
                  <Icon className={`w-5 h-5 ${isActive ? "text-accent" : "text-slate-400"}`} />
                </div>
                <h3 className={`text-base font-bold mb-1 ${isActive ? "text-slate-900" : "text-slate-600"}`}>
                  {axe.title}
                </h3>
                <p className="text-xs text-slate-500">{axe.subtitle}</p>
              </button>
            );
          })}
        </div>

        {/* Active Axe Services */}
        {axes.map((axe) => {
          if (axe.id !== openAxe) return null;
          return (
            <div key={axe.id} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`services-${axe.id}`}>
              {axe.services.map((service, index) => {
                const SIcon = service.icon;
                return (
                  <div
                    key={service.title}
                    className="btp-card p-6 transition-all duration-300"
                    data-testid={`solution-card-${index}`}
                  >
                    <div className="w-12 h-12 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                      <SIcon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                      {service.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {service.tags.map((tag) => (
                        <span key={tag} className="text-xs font-medium bg-slate-100 text-slate-500 px-2 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Solution;
